/**
 * Sidebar Component
 *
 * Боковое меню портала
 * - Сворачиваемое (280px → 60px)
 * - Раскрывающиеся секции: Sales, Activities, Reports, Settings
 * - Использует NavSection и NavItem компоненты
 * - Фильтрация пунктов меню по правам доступа
 */

import React from 'react';
import { AppShell, Stack, Box, ActionIcon, Tooltip, ScrollArea, Text } from '@mantine/core';
import {
  IconBuilding, IconUsers, IconChartBar, IconFileText,
  IconCalendar, IconMail, IconPhone, IconFolder,
  IconChevronLeft, IconChevronRight, IconKey, IconSettings,
  IconShoppingCart, IconClipboardList, IconReportAnalytics,
  IconUserCog, IconTemplate, IconBook, IconBriefcase,
  IconLayoutDashboard, IconTags, IconSchool, IconCalendarStats, IconCurrencyRubel,
  IconCreditCard, IconTrophy
} from '@tabler/icons-react';
import NavSection from './NavSection';
import NavItem from './NavItem';
import tokens from '../theme/designTokens';
import { usePermissions } from '../hooks/usePermissions';

// Импорт версии с fallback
let versionInfo = { version: 'dev' };
try {
  versionInfo = require('../generated/version.json');
} catch (e) {
  // Файл версии не найден, используем fallback
}

const Sidebar = ({ collapsed, onToggle, onMobileClose }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  // Проверка доступа к секциям
  const canAccessSales = hasAnyPermission([
    'organizations:read',
    'contacts:read',
    'students:read',
    'parents:read',
    'deals:read',
  ]);

  const canAccessActivities = hasAnyPermission([
    'calendar:read',
    'emails:read',
    'calls:read',
    'groups:read',
  ]);

  const canAccessReports = hasPermission('reports:read');
  const canAccessSettings = hasPermission('settings:read');

  return (
    <AppShell.Navbar
      p={0}
      style={{
        backgroundColor: 'white',
        borderRight: `1px solid ${tokens.colors.neutral[3]}`,
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Toggle Button - фиксированный, скрыт на мобильных */}
      <Box
        visibleFrom="sm"
        style={{
          padding: '12px',
          borderBottom: `1px solid ${tokens.colors.neutral[3]}`,
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
          flexShrink: 0,
        }}
      >
        <Tooltip label={collapsed ? 'Развернуть' : 'Свернуть'} position="right" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onToggle}
          >
            {collapsed ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Скроллируемая область меню */}
      <ScrollArea style={{ flex: 1 }} scrollbarSize={6}>
        <Stack gap={0}>
          {/* Рабочее место (Dashboard) - для всех авторизованных */}
          <NavItem
            icon={<IconLayoutDashboard size={20} />}
            label="Рабочее место"
            to="/"
            collapsed={collapsed}
            onClick={onMobileClose}
          />

          {/* Секция SALES - только для ролей с доступом к организациям */}
          {canAccessSales && (
          <NavSection
            icon={<IconShoppingCart size={20} />}
            label="Sales"
            collapsed={collapsed}
            defaultOpen={true}
          >
            {hasPermission('organizations:read') && (
              <NavItem icon={<IconBuilding size={20} />} label="Организации" to="/organizations" collapsed={collapsed} onClick={onMobileClose} />
            )}
            {hasPermission('contacts:read') && (
              <NavItem icon={<IconUsers size={20} />} label="Контакты" to="/contacts" collapsed={collapsed} onClick={onMobileClose} />
            )}
            {hasPermission('students:read') && (
              <NavItem icon={<IconSchool size={20} />} label="Ученики" to="/students" collapsed={collapsed} onClick={onMobileClose} />
            )}
            {hasPermission('parents:read') && (
              <NavItem icon={<IconUsers size={20} />} label="Родители" to="/parents" collapsed={collapsed} onClick={onMobileClose} />
            )}
          </NavSection>
        )}

        {/* Секция ACTIVITIES - для ролей с доступом к активностям */}
        {canAccessActivities && (
          <NavSection
            icon={<IconClipboardList size={20} />}
            label="Activities"
            collapsed={collapsed}
            defaultOpen={true}
          >
            {hasPermission('calendar:read') && (
              <NavItem icon={<IconCalendar size={20} />} label="Календарь" to="/calendar" collapsed={collapsed} onClick={onMobileClose} />
            )}
            {hasPermission('groups:read') && (
              <>
                <NavItem icon={<IconCalendarStats size={20} />} label="Расписание" to="/schedule" collapsed={collapsed} onClick={onMobileClose} />
                <NavItem icon={<IconTrophy size={20} />} label="Соревнования" to="/competitions" collapsed={collapsed} onClick={onMobileClose} />
              </>
            )}
            {/* Email и Звонки — будут добавлены позже */}
          </NavSection>
        )}

        {/* Секция REPORTS - для ролей с доступом к отчетам */}
        {canAccessReports && (
          <NavSection
            icon={<IconReportAnalytics size={20} />}
            label="Reports"
            collapsed={collapsed}
            defaultOpen={true}
          >
            <NavItem icon={<IconChartBar size={20} />} label="Дашборды" to="/reports/dashboards" collapsed={collapsed} onClick={onMobileClose} />
          </NavSection>
        )}

        {/* Секция SETTINGS - только для администраторов */}
        {canAccessSettings && (
          <NavSection
            icon={<IconSettings size={20} />}
            label="Settings"
            collapsed={collapsed}
            defaultOpen={true}
          >
            <NavItem icon={<IconKey size={20} />} label="API Токены" to="/settings/api-tokens" collapsed={collapsed} onClick={onMobileClose} />
            {hasPermission('users:read') && (
              <NavItem icon={<IconUserCog size={20} />} label="Пользователи" to="/settings/users" collapsed={collapsed} onClick={onMobileClose} />
            )}
            <NavItem icon={<IconTemplate size={20} />} label="Шаблоны сообщений" to="/settings/message-templates" collapsed={collapsed} onClick={onMobileClose} />
            <NavItem icon={<IconBook size={20} />} label="Предметы" to="/settings/subjects" collapsed={collapsed} onClick={onMobileClose} />
            <NavItem icon={<IconCurrencyRubel size={20} />} label="Стоимость занятий" to="/settings/pricing" collapsed={collapsed} onClick={onMobileClose} />
            <NavItem icon={<IconCreditCard size={20} />} label="Платежи" to="/settings/payments" collapsed={collapsed} onClick={onMobileClose} />
            <NavItem icon={<IconBriefcase size={20} />} label="Должности партнёров" to="/settings/partner-positions" collapsed={collapsed} onClick={onMobileClose} />
            <NavItem icon={<IconTags size={20} />} label="Статусы организаций" to="/settings/partnership-statuses" collapsed={collapsed} onClick={onMobileClose} />
          </NavSection>
        )}
        </Stack>
      </ScrollArea>

      {/* Версия приложения */}
      <Box
        style={{
          padding: collapsed ? '8px 4px' : '12px 16px',
          borderTop: `1px solid ${tokens.colors.neutral[3]}`,
          flexShrink: 0,
        }}
      >
        <Text
          size="xs"
          c="dimmed"
          ta={collapsed ? 'center' : 'left'}
          style={{
            fontSize: collapsed ? '9px' : '11px',
            letterSpacing: '0.5px',
          }}
        >
          {collapsed ? versionInfo.version.replace('v', '') : versionInfo.version}
        </Text>
      </Box>
    </AppShell.Navbar>
  );
};

export default Sidebar;
